import React, { useEffect, useRef } from 'react';

interface ServerErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
}

const ServerErrorModal: React.FC<ServerErrorModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
}) => {
  const modal = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    onClose();
  };

  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      if (modal.current && !modal.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [isOpen, onClose]);

  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (isOpen && (keyCode === 27 || keyCode === 13)) {
        handleClose();
      }
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5 ${
        isOpen ? 'block' : 'hidden'
      }`}
    >
      <div
        ref={modal}
        className="w-full max-w-md rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark"
      >
        {/* Ícono de error de servidor */}
        <div className="mb-4 flex justify-center">
          <div className="rounded-full bg-[#FCEDEA] p-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DC3545] text-white">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                id="Outline"
                viewBox="0 0 24 24"
                fill="none"
                width="24"
                height="24"
              >
                <path
                  fill="white"
                  stroke="white"
                  d="m22.242,5.272l-3.515-3.515c-1.133-1.133-2.64-1.757-4.242-1.757h-4.971c-1.602,0-3.109.624-4.243,1.757l-3.515,3.515c-1.133,1.134-1.757,2.641-1.757,4.243v4.971c0,1.602.624,3.109,1.757,4.243l3.515,3.515c1.134,1.133,2.641,1.757,4.243,1.757h4.971c1.603,0,3.109-.624,4.242-1.757l3.515-3.514c1.134-1.133,1.758-2.64,1.758-4.243v-4.971c0-1.603-.624-3.11-1.758-4.243Zm-.242,9.213c0,1.069-.416,2.073-1.172,2.829l-3.515,3.515c-.756.755-1.76,1.171-2.828,1.171h-4.971c-1.068,0-2.073-.416-2.829-1.171l-3.514-3.515c-.756-.756-1.172-1.76-1.172-2.829v-4.971c0-1.068.416-2.073,1.171-2.829l3.515-3.514c.756-.756,1.76-1.172,2.829-1.172h4.971c1.068,0,2.072.416,2.828,1.171l3.515,3.515c.756.755,1.172,1.759,1.172,2.828v4.971Zm-5.561-5.511l-3.043,3.043,3.043,3.043c.391.391.391,1.023,0,1.414-.195.195-.451.293-.707.293s-.512-.098-.707-.293l-3.043-3.043-3.043,3.043c-.195.195-.451.293-.707.293s-.512-.098-.707-.293c-.391-.391-.391-1.023,0-1.414l3.043-3.043-3.043-3.043c-.391-.391-.391-1.023,0-1.414s1.023-.391,1.414,0l3.043,3.043,3.043-3.043c.391-.391,1.023-.391,1.414,0s.391,1.023,0,1.414Z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Título */}
        <h3 className="pb-2 text-xl font-bold text-[#DC3545] dark:text-[#DC3545] sm:text-2xl">
          {title}
        </h3>

        {/* Mensaje */}
        <p className="text-gray-800 dark:text-gray-300 mb-6">{message}</p>

        {/* Botón */}
        <button
          onClick={handleClose}
          className="block w-full rounded border border-[#DC3545] bg-[#DC3545] p-3 text-center font-medium text-white transition hover:bg-opacity-90"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default ServerErrorModal;
